import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, CreditCard, Banknote, Loader2, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { CustomerLayout } from '@/components/layout/CustomerLayout';
import { useCart } from '@/context/CartContext';
import { createOrder } from '@/lib/api';
import { pushOrderToHistory } from '@/lib/orderHistory';
import { toast } from 'sonner';

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { cartItems, cartTotal, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    customer_name: '',
    phone: '',
    email: '',
    address: '',
    pincode: '',
    payment_method: 'cod'
  });

  const deliveryFee = cartTotal >= 199 ? 0 : 30;
  const grandTotal = cartTotal + deliveryFee;

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name || !formData.phone || !formData.address || !formData.pincode) {
      toast.error('Please fill all required fields', { id: 'checkout-validation' });
      return;
    }

    if (!/^[6-9]\d{9}$/.test(formData.phone)) {
      toast.error('Please enter a valid 10-digit Indian mobile number', { id: 'checkout-validation' });
      return;
    }

    if (!/^[1-9]\d{5}$/.test(formData.pincode)) {
      toast.error('Please enter a valid 6-digit Indian pincode', { id: 'checkout-validation' });
      return;
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Please enter a valid email or leave it empty', { id: 'checkout-validation' });
      return;
    }

    if (cartItems.length === 0) {
      toast.error('Your cart is empty', { id: 'checkout-validation' });
      return;
    }

    setLoading(true);
    
    try {
      const orderData = {
        ...formData,
        email: formData.email || null,
        items: cartItems.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity
        }))
      };

      const response = await createOrder(orderData);
      pushOrderToHistory(response.data.id, response.data.view_token, formData.phone);
      clearCart();
      toast.success('Order placed successfully!');
      navigate(`/order-confirmation/${response.data.id}`);
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <CustomerLayout>
        <div className="px-4 py-12 max-w-md mx-auto text-center">
          <h2 className="text-xl font-bold mb-4">Your cart is empty</h2>
          <Link to="/products">
            <Button className="rounded-full">Start Shopping</Button>
          </Link>
        </div>
      </CustomerLayout>
    );
  }

  return (
    <CustomerLayout>
      <div className="px-4 py-4 max-w-2xl mx-auto" data-testid="checkout-page">
        {/* Header */}
        <div className="flex items-center gap-2 mb-6">
          <Link to="/cart">
            <Button variant="ghost" size="icon" className="rounded-full">
              <ChevronLeft className="w-5 h-5" />
            </Button>
          </Link>
          <h1 className="text-xl md:text-2xl font-bold">Checkout</h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Privacy trust message */}
          <div
            className="bg-primary/5 border border-primary/20 rounded-xl p-4 flex items-start gap-3"
            data-testid="privacy-trust-banner"
          >
            <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
              <Lock className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="font-semibold text-sm text-foreground">Your details are private</p>
              <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                Your personal information is secure and will only be used by QGT for order fulfillment.
                We never share your details with other customers.
              </p>
            </div>
          </div>

          {/* Delivery Details */}
          <div className="bg-white rounded-xl border border-border p-4">
            <h2 className="font-semibold mb-4">Delivery Details</h2>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="customer_name">Full Name *</Label>
                <Input
                  id="customer_name"
                  name="customer_name"
                  value={formData.customer_name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  className="mt-1.5 h-12 rounded-lg"
                  required
                  data-testid="input-name"
                />
              </div>

              <div>
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="10-digit mobile number"
                  className="mt-1.5 h-12 rounded-lg"
                  maxLength={10}
                  required
                  data-testid="input-phone"
                />
              </div>

              <div>
                <Label htmlFor="email">Email <span className="text-muted-foreground font-normal text-xs">(optional — for order updates)</span></Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  className="mt-1.5 h-12 rounded-lg"
                  data-testid="input-email"
                />
              </div>

              <div>
                <Label htmlFor="pincode">Pincode *</Label>
                <Input
                  id="pincode"
                  name="pincode"
                  type="tel"
                  inputMode="numeric"
                  value={formData.pincode}
                  onChange={(e) => setFormData(prev => ({ ...prev, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                  placeholder="6-digit pincode (e.g., 524001)"
                  className="mt-1.5 h-12 rounded-lg"
                  maxLength={6}
                  required
                  data-testid="input-pincode"
                />
              </div>

              <div>
                <Label htmlFor="address">Delivery Address *</Label>
                <Textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="House/Flat No, Street, Landmark, Town"
                  className="mt-1.5 min-h-[100px] rounded-lg"
                  required
                  data-testid="input-address"
                />
              </div>
            </div>
          </div>

          {/* Payment Method */}
          <div className="bg-white rounded-xl border border-border p-4">
            <h2 className="font-semibold mb-4">Payment Method</h2>
            
            <RadioGroup
              value={formData.payment_method}
              onValueChange={(value) => setFormData(prev => ({ ...prev, payment_method: value }))}
              className="space-y-3"
            >
              <label 
                className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${
                  formData.payment_method === 'cod' 
                    ? 'border-primary bg-primary/5' 
                    : 'border-border hover:border-primary/50'
                }`}
                data-testid="payment-cod"
              >
                <RadioGroupItem value="cod" id="cod" />
                <Banknote className="w-6 h-6 text-primary" />
                <div className="flex-1">
                  <p className="font-medium">Cash on Delivery</p>
                  <p className="text-xs text-muted-foreground">Pay when you receive</p>
                </div>
                <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">Popular</span>
              </label>

              <label 
                className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${
                  formData.payment_method === 'upi' 
                    ? 'border-primary bg-primary/5' 
                    : 'border-border hover:border-primary/50'
                }`}
                data-testid="payment-upi"
              >
                <RadioGroupItem value="upi" id="upi" />
                <CreditCard className="w-6 h-6 text-primary" />
                <div className="flex-1">
                  <p className="font-medium">UPI Payment</p>
                  <p className="text-xs text-muted-foreground">GPay, PhonePe, Paytm</p>
                </div>
              </label>
            </RadioGroup>
          </div>

          {/* Order Summary */}
          <div className="bg-white rounded-xl border border-border p-4">
            <h2 className="font-semibold mb-4">Order Summary</h2>
            
            <div className="space-y-3 text-sm">
              {cartItems.map((item) => (
                <div key={item.product_id} className="flex justify-between">
                  <span className="text-muted-foreground">
                    {item.name} x {item.quantity}
                  </span>
                  <span>₹{(item.price * item.quantity).toFixed(0)}</span>
                </div>
              ))}
              
              <div className="border-t border-border pt-3 space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>₹{cartTotal.toFixed(0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Delivery</span>
                  <span className={deliveryFee === 0 ? 'text-primary' : ''}>
                    {deliveryFee === 0 ? 'FREE' : `₹${deliveryFee}`}
                  </span>
                </div>
                <div className="flex justify-between font-bold text-base pt-2 border-t border-border">
                  <span>Total</span>
                  <span className="text-primary">₹{grandTotal.toFixed(0)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="sticky bottom-20 md:bottom-4 bg-background py-3 -mx-4 px-4 border-t border-border md:border-none md:bg-transparent">
            <Button 
              type="submit" 
              className="w-full h-14 rounded-full text-base font-semibold"
              disabled={loading}
              data-testid="place-order-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Placing Order...
                </>
              ) : (
                <>
                  Place Order
                  <span className="ml-2 bg-white/20 px-3 py-1 rounded-full text-sm">
                    ₹{grandTotal.toFixed(0)}
                  </span>
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </CustomerLayout>
  );
}
